/**
 * DataCollectionSDK.
 * Combines all provided functionality of Collection platform in one place and setup correct dependencies between them
 *
 * @copyright Realeyes OU. All rights reserved.
 */

import EventTracker from './components/EventTracker';
import ConfigurationManager from './components/ConfigurationManager';
import IdentityManager from './components/IdentityManager';
import TimeSyncService from './components/TimeSyncService';
import Logger from './components/Logger';
import MediaDeviceRecorder from './components/MediaDeviceRecorder';
import LogMessageTracker from './components/LogMessageTracker';

export default class SDK {
    /**
     * @param {Object} config is on fly defined configuration of Collection SDK
     * Creates SDK instance.
     */
    constructor(config) {
        this.configurationManager = new ConfigurationManager(config);
        this.timeSyncService = new TimeSyncService(this.configurationManager);
        this.eventTrackers = [];
        this.logMessageTrackers = [];
        this.logger = new Logger('SDK', this.configurationManager.isDebugMode());
        this.identityService = new IdentityManager(this.configurationManager);
    }

    /**
     * Returns an instance of MediaDeviceRecorder.
     * @return {Promise<MediaDeviceRecorder>}
     */
    getMediaDeviceRecorder() {
        return new Promise((resolve) => {
            if (this.mediaDeviceRecorder) {
                resolve(this.mediaDeviceRecorder);
            } else {
                this.mediaDeviceRecorder = new MediaDeviceRecorder(this.configurationManager);
                if (this.mediaDeviceRecorder.config.trackLogs && this.mediaDeviceRecorder.logger) {
                    this.registerLogMessageTracker('messages').then((tracker) => {
                        this.mediaDeviceRecorder.logger.setLogTracker(tracker);
                    }, this.logger.logError);
                }
                resolve(this.mediaDeviceRecorder);
            }
        });
    }

    /**
     * Register an eventTracker and return instance of it.
     *
     * @param {string} name of registered tracker, will be used as stream name
     * @return {Promise.<EventTracker, Error>}
     */
    registerEventTracker(name) {
        return new Promise((resolve, reject) => {
            this.logger.logDebug(`Request to register ET with name '${name}'`);
            if (!name) {
                const msg = 'eventTracker name is not provided!';
                this.logger.logError(msg);
                reject(new Error(msg));
            }

            let tracker = this.eventTrackers.find((a) => a.name === name);
            if (!tracker) {
                tracker = new EventTracker(name, this.identityService,
                    this.configurationManager, this.timeSyncService);
                this.eventTrackers.push(tracker);
                this.logger.logDebug(`ET with name '${name}' has been registred`);
            }

            this.logger.logDebug(`ET with name '${name}' returned`);
            resolve(tracker);
        });
    }

    /**
     * Register an logMessageTracker and return instance of it.
     *
     * @param {string} name of registered tracker, will be used as stream name
     * @return {Promise.<LogMessageTracker, Error>}
     */
    registerLogMessageTracker(name) {
        return new Promise((resolve, reject) => {
            this.logger.logDebug(`Request to register LT with name '${name}'`);
            if (!name) {
                const msg = 'logMessageTracker name is not provided!';
                this.logger.logError(msg);
                reject(new Error(msg));
            }

            let tracker = this.logMessageTrackers.find((a) => a.name === name);
            if (!tracker) {
                tracker = new LogMessageTracker(name, this.identityService,
                    this.configurationManager, this.timeSyncService);
                this.logMessageTrackers.push(tracker);
                this.logger.logDebug(`LT with name '${name}' has been registred`);
            }

            this.logger.logDebug(`LT with name '${name}' returned`);
            resolve(tracker);
        });
    }
}
